import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatPriceCents, parsePriceInput } from "../service-meta";

/**
 * Preço editável na própria linha. Sem preço não é "R$ 0,00" (lê como grátis): é "Definir preço" em
 * âmbar. O PIN, quando falta, é pedido pelo link do tRPC, como em qualquer gravação de preço.
 */
export function PrecoNaLinha({ id, priceCents, podeEditar }: { id: string; priceCents: number; podeEditar: boolean }) {
  const utils = trpc.useUtils();
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState("");
  const salvar = trpc.service.update.useMutation({
    onSuccess: () => {
      utils.service.list.invalidate();
      utils.service.getById.invalidate({ id });
      setEditando(false);
    },
    onError: (e) => toast.error(e.message || "Não foi possível salvar o preço."),
  });
  const semPreco = priceCents === 0;

  if (!podeEditar) {
    return <span className={cn("tabular-nums", semPreco && "text-zinc-400")}>{semPreco ? "Sem preço" : formatPriceCents(priceCents)}</span>;
  }
  if (editando) {
    const gravar = () => {
      const centavos = parsePriceInput(valor);
      if (centavos === priceCents) setEditando(false);
      else salvar.mutate({ id, priceCents: centavos });
    };
    return (
      <span className="inline-flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {salvar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
        <input
          autoFocus
          inputMode="decimal"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onBlur={gravar}
          onKeyDown={(e) => {
            if (e.key === "Enter") gravar();
            if (e.key === "Escape") setEditando(false);
          }}
          aria-label="Preço"
          className="h-8 w-28 rounded-md border border-brand-500 bg-white px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-500 dark:bg-card"
        />
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setValor(semPreco ? "" : (priceCents / 100).toFixed(2).replace(".", ","));
        setEditando(true);
      }}
      title="Editar preço"
      className={cn(
        "rounded-md px-2 py-1 tabular-nums transition-colors",
        semPreco
          ? "bg-amber-50 text-[12px] font-semibold text-amber-700 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-400"
          : "text-zinc-700 hover:bg-zinc-100 dark:text-foreground/80 dark:hover:bg-muted",
      )}
    >
      {semPreco ? "Definir preço" : formatPriceCents(priceCents)}
    </button>
  );
}
